import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import store from '@/store';
import { EAccountTypes } from '@/types';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'Login',
    meta: { layout: 'index', requiresAuth: false },
    component: () => import('@/components/user/Login.vue'),
  },
  {
    path: '/recovery',
    name: 'Recovery',
    meta: { layout: 'index', requiresAuth: false },
    component: () => import('@/components/user/Recovery.vue'),
  },
  {
    path: '/register',
    name: 'Register',
    meta: { layout: 'index', requiresAuth: false },
    component: () => import('@/components/user/Register.vue'),
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    meta: { layout: 'index', requiresAuth: false },
    component: () => import('@/components/user/PrivacyPolicy.vue'),
  },
  {
    path: '/service-agreement',
    name: 'ServiceAgreement',
    meta: { layout: 'index', requiresAuth: false },
    component: () => import('@/components/user/ServiceAgreement.vue'),
  },
  {
    path: '/mail-confirm',
    name: 'ConfirmedEmailPage',
    meta: { layout: 'empty', requiresAuth: false },
    component: () => import('@/components/user/ConfirmedEmailPage.vue'),
  },
  {
    path: '/account',
    name: 'Account',
    meta: { layout: 'main', requiresAuth: true },
    component: () => import('@/pages/Account.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'dashboard',
    },
    component: () => import('@/pages/AdvertiserDashboard.vue'),
  },
  {
    path: '/campaign/create',
    name: 'CreateCampaign',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'campaign',
    },
    component: () => import('@/pages/CreateCampaign.vue'),
  },
  {
    path: '/campaign/update/:id',
    name: 'UpdateCampaign',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'campaign',
    },
    props: true,
    component: () => import('@/pages/UpdateCampaign.vue'),
  },
  {
    path: '/campaign/clone/:id',
    name: 'CloneCampaign',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'campaign',
    },
    props: true,
    component: () => import('@/pages/CloneCampaign.vue'),
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'campaign',
    },
    props: true,
    component: () => import('@/pages/Campaigns.vue'),
  },
  {
    path: '/group/create',
    name: 'CreateCampaignGroup',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'group',
    },
    props: true,
    component: () => import('@/pages/group/CreateCampaignGroup.vue'),
  },
  {
    path: '/group/update/:id',
    name: 'UpdateCampaignGroup',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'group',
    },
    props: true,
    component: () => import('@/pages/group/UpdateCampaignGroup.vue'),
  },
  {
    path: '/group/clone/:id',
    name: 'CloneCampaignGroup',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'group',
    },
    props: true,
    component: () => import('@/pages/group/CloneCampaignGroup.vue'),
  },
  {
    path: '/groups',
    name: 'CampaignGroups',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'group',
    },
    props: true,
    component: () => import('@/pages/group/CampaignGroups.vue'),
  },
  {
    path: '/moderate/creative',
    name: 'ModerationCreative',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'moderation',
    },
    props: true,
    component: () => import('@/pages/ModerationCreative.vue'),
  },
  {
    path: '/statistics/ssp',
    name: 'SspStatistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/SspStatistics.vue'),
  },
  {
    path: '/ssp/statistics',
    name: 'Statistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/ssp/Statistics.vue'),
  },
  {
    path: '/statistics/campaign',
    name: 'CampaignStatistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/CampaignStatistics.vue'),
  },
  {
    path: '/statistics/creative',
    name: 'CreativeStatistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/CreativeStatistics.vue'),
  },
  {
    path: '/statistics/lead',
    name: 'LeadStatistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/LeadStatistics.vue'),
  },
  {
    path: '/statistics/site',
    name: 'SiteStatistics',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'statistic',
    },
    props: true,
    component: () => import('@/pages/SiteStatistics.vue'),
  },
  {
    path: '/publisher/inventory',
    name: 'PublisherInventory',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.PUBLISHER,
      group: 'inventory',
    },
    props: true,
    component: () => import('@/pages/publisher/Inventory.vue'),
  },
  {
    path: '/publisher/inventory/create',
    name: 'CreateInventory',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.PUBLISHER,
      group: 'inventory',
    },
    props: true,
    component: () => import('@/pages/publisher/CreateInventory.vue'),
  },
  {
    path: '/publisher/inventory/update/:id',
    name: 'UpdateInventory',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.PUBLISHER,
      group: 'inventory',
    },
    props: true,
    component: () => import('@/pages/publisher/UpdateInventory.vue'),
  },
  {
    path: '/publisher/placement/:id',
    name: 'CreatePlacement',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.PUBLISHER,
      group: 'inventory',
    },
    props: true,
    component: () => import('@/pages/publisher/CreatePlacement.vue'),
  },
  {
    path: '/audiences',
    name: 'Audiences',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'audience',
    },
    props: true,
    component: () => import('@/pages/audiences/Audiences.vue'),
  },
  {
    path: '/audiences/create',
    name: 'CreateAudience',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'audience',
    },
    props: true,
    component: () => import('@/pages/audiences/CreateAudience.vue'),
  },
  {
    path: '/audiences/update/:id',
    name: 'UpdateAudience',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'audience',
    },
    props: true,
    component: () => import('@/pages/audiences/UpdateAudience.vue'),
  },
  {
    path: '/report-builder',
    name: 'ReportBuilder',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'analitics',
    },
    props: true,
    component: () => import('@/pages/ReportBuilder.vue'),
  },
  {
    path: '/exist-reports',
    name: 'ExistReports',
    meta: {
      layout: 'main',
      requiresAuth: true,
      accountType: EAccountTypes.ADVERTISER,
      group: 'analitics',
    },
    props: true,
    component: () => import('@/pages/ExistReports.vue'),
  },
  {
    path: '*',
    name: 'NotFound',
    meta: { layout: 'empty', requiresAuth: true },
    component: () => import('@/pages/NotFound.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.path === '/mail-confirm') {
    next();
    return;
  }

  const userIsAuth = store.getters.hasToken;
  const requireAuth = to.matched.some((record) => record.meta.requiresAuth);
  switch (true) {
    case requireAuth && !userIsAuth:
      next('/login');
      break;
    case !requireAuth && userIsAuth:
      if (store.getters.userCan('manager')) {
        next('/dashboard');
      } else if (store.getters.userCan('ssp')) {
        next('/moderate/creative');
      } else if (store.getters.userCan('publisher')) {
        next('/publisher/inventory');
      } else {
        next();
      }
      break;
    default:
      next();
  }
});

export default router;
